import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Empty, StatusBadge } from "../components/ui";
import PDFButton from "../components/PDFButton";
import ReportButton from "../components/ReportButton";
import Sparkline from "../components/Sparkline";
import DashboardDrillDown from "../components/DashboardDrillDown";
import TrackingPanel from "../components/TrackingPanel";
import {
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  Tooltip, BarChart, Bar, Legend, CartesianGrid
} from "recharts";
import { RefreshCw, FileText, Eye, AlertTriangle, X, ChevronDown, ChevronUp } from "lucide-react";

// Maps alert/notification "module" + "type" → frontend path. Append #id so the
// destination page can highlight/open the specific record.
const MODULE_ROUTE = {
  legal: "/legal",
  legal_permits: "/legal",
  periodic_reports: "/legal",
  audits: "/audits",
  racs: "/audits",
  field_walks: "/audits",
  findings: "/audits",
  toa_cards: "/audits",
  inspections: "/iso14001",
  studies: "/studies",
  wells: "/resources",
  well_payments: "/resources",
  well_authorizations: "/resources",
  resources: "/resources",
  waste_logs: "/waste",
  waste: "/waste",
  esg_complaints: "/esg",
  esg: "/esg",
  brigades: "/brigades",
  emergency_reports: "/brigades",
  procedures: "/iso14001",
  iso_objectives: "/iso14001",
  advc_records: "/advc",
  smart_projects: "/smart",
  providers: "/providers",
  inductions: "/communication",
  map_areas: "/map",
};

function navigateToAlert(navigate, a) {
  const key = a.module || a.type || "";
  const route = MODULE_ROUTE[key] || "/dashboard";
  const id = a.id || a.item_id;
  navigate(id ? `${route}?focus=${encodeURIComponent(id)}` : route);
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [alertsOpen, setAlertsOpen] = useState(true);
  const [heatmap, setHeatmap] = useState([]);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [drill, setDrill] = useState(null);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 4; y--) yearOptions.push(y);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [d, a, h] = await Promise.all([
        api.get("/dashboard/summary", { params: { year } }),
        api.get("/calendar/alerts"),
        api.get("/calendar/heatmap"),
      ]);
      setData(d.data); setAlerts(a.data); setHeatmap(h.data);
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds — silent (does NOT toggle `loading`, so the
  // DOM is not unmounted/replaced with "Cargando..." and the user's scroll
  // position / section is preserved while working).
  useEffect(() => {
    const t = setInterval(() => { load({ silent: true }); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading || !data) {
    return <div className="card text-dim">Cargando dashboard...</div>;
  }

  const ecsColor = data.ecs_score >= 85 ? "var(--success)" : data.ecs_score >= 60 ? "var(--warn)" : "var(--danger)";

  return (
    <div data-testid="dashboard">
      {/* Critical alerts banner */}
      {alerts.length > 0 && (
        <div className="card mb-4" style={{ borderLeft: "4px solid var(--danger)", background: "rgba(231,76,60,0.08)" }} data-testid="alerts-banner">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => setAlertsOpen(!alertsOpen)}>
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-danger" />
              <span className="font-bold text-danger">{alerts.length} alertas críticas</span>
            </div>
            <button className="btn btn-sm btn-ghost" data-testid="alerts-toggle">
              {alertsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
          {alertsOpen && (
            <div className="mt-3 grid gap-2">
              {alerts.slice(0, 8).map((a, i) => (
                <div
                  key={a.id || `alert-${i}-${(a.message || "").slice(0, 16)}`}
                  className="flex items-center justify-between p-2 rounded"
                  style={{ background: "rgba(231,76,60,0.06)", cursor: "pointer" }}
                  onClick={() => navigateToAlert(navigate, a)}
                  data-testid={`alert-row-${i}`}
                  title="Ir al registro de origen"
                >
                  <div className="text-sm">⚠ {a.title}</div>
                  <span className="badge badge-red">{a.severity}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Header actions */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h1 className="card-h1" style={{ margin: 0 }}>Panel Principal</h1>
        <div className="flex gap-2 items-center">
          <select
            className="select"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            style={{ width: 130 }}
            data-testid="dashboard-year-select"
            title="Año fiscal mostrado en sparklines"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y === currentYear ? `${y} (actual)` : y}</option>
            ))}
          </select>
          <button
            className="btn btn-sm"
            onClick={() => navigate("/audit-mode")}
            data-testid="dashboard-audit-mode"
            style={{ background: "var(--danger)", color: "#fff", animation: "auditPulse 2s ease-in-out infinite" }}
          >
            <Eye size={14} /> Modo Auditoría
          </button>
          <PDFButton endpoint="/pdf/one-pager" filename="one_pager.pdf" label="One-Pager Ejecutivo" testId="dashboard-one-pager" />
          <PDFButton endpoint="/pdf/global" filename="reporte_global.pdf" label="Reporte Global" testId="dashboard-global" extraBody={{ include: ["summary","indicators","legal","wells","ptar","waste","iso","audits","studies","esg","brigades","advc"], period: "YTD" }} />
          <ReportButton
            customUrl="/reports/executive-consolidated"
            filename="reporte_ejecutivo_consolidado"
            label="Reporte Ejecutivo Consolidado"
            testId="dashboard-exec-consolidated"
          />
          <button className="btn btn-sm btn-primary" onClick={load} data-testid="dashboard-refresh"><RefreshCw size={14} /> Actualizar</button>
        </div>
      </div>

      {/* ICA — full width (KPIs moved into their own indicator groups: legal, racs, iso14001, studies) */}
      <div className="grid mb-4" style={{ gridTemplateColumns: "1fr", gap: 16 }}>
        <div
          className="ecs-card"
          data-testid="ica-card"
          style={{ cursor: "pointer" }}
          onClick={() => setDrill({
            kind: "kpi", key: "ica", label: "Índice de Cumplimiento Ambiental (ICA)",
            subtitle: "Composición y subíndices que componen el indicador",
            kpiData: data,
            route: { path: "/legal", state: { tab: "evaluacion" } },
          })}
          onMouseEnter={(e) => (e.currentTarget.style.border = "1px solid rgba(245,184,46,0.5)")}
          onMouseLeave={(e) => (e.currentTarget.style.border = "")}
          title="Ver desglose del ICA"
        >
          <div className="card-title" style={{ marginBottom: 8 }} title="Índice de Cumplimiento Ambiental">ICA · Índice de Cumplimiento Ambiental</div>
          <div className="ecs-gauge" style={{ maxWidth: 360, margin: "0 auto" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[{ v: data.ecs_score }, { v: 100 - data.ecs_score }]}
                  dataKey="v"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={60}
                  outerRadius={95}
                  cy="100%"
                  isAnimationActive={false}
                >
                  <Cell fill={ecsColor} />
                  <Cell fill="var(--bg-3)" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="ecs-value" style={{ color: ecsColor }}>{data.ecs_score}</div>
          </div>
          <div className="ecs-sub" style={{ textAlign: "center" }}>
            Δ {data.ecs_delta >= 0 ? "+" : ""}{data.ecs_delta} pts · {data.days_without_incident} días sin incumplimiento
          </div>
        </div>
      </div>

      <IndicatorGroups indicators={data.indicators} trendChart={data.trend_chart} onDrill={(ind, route) => setDrill({ kind: "indicator", key: ind.key, label: ind.label, subtitle: `Unidad: ${ind.unit || "—"}`, indicator: ind, route })} />

      {/* Pending + Upcoming */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="card">
          <div className="card-title">Acciones pendientes (Top 5)</div>
          {!data.pending_actions.length ? <Empty title="Sin pendientes" sub="Excelente trabajo." /> : (
            <div className="grid gap-2">
              {data.pending_actions.map((a, i) => (
                <div key={a.id || `act-${i}-${(a.title || "").slice(0, 16)}`} className="flex items-center justify-between gap-3 p-2 rounded" style={{ background: "var(--bg-2)" }}>
                  <div className="flex-1" style={{ minWidth: 0 }}>
                    <div className="text-sm font-semibold">{a.title}</div>
                    <div className="text-xs text-dim mt-2">{a.type} · vence {a.due_date || "sin fecha"}</div>
                  </div>
                  <StatusBadge value={a.severity || "Pendiente"} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-title">Próximas fechas críticas</div>
          {!data.upcoming_dates.length ? <Empty title="Sin próximos vencimientos" /> : (
            <div className="grid gap-2">
              {data.upcoming_dates.map((d, i) => (
                <div key={d.id || `up-${i}-${(d.title || "").slice(0, 16)}`} className="flex items-center justify-between gap-3 p-2 rounded" style={{ background: "var(--bg-2)" }}>
                  <div className="flex-1" style={{ minWidth: 0 }}>
                    <div className="text-sm font-semibold">{d.title}</div>
                    <div className="text-xs text-dim mt-2">{d.type} · {d.date}</div>
                  </div>
                  <span className="badge badge-blue">{daysUntil(d.date)} días</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Regulatory heat calendar (collapsible) */}
      <div className="card" data-testid="heatmap-card">
        <div className="card-title cursor-pointer" onClick={() => setShowHeatmap(!showHeatmap)} style={{ cursor: "pointer" }}>
          <span>Calendario Regulatorio de Calor ({heatmap.length} obligaciones próximas)</span>
          <button className="btn btn-sm btn-ghost" data-testid="heatmap-toggle">{showHeatmap ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</button>
        </div>
        {showHeatmap && <HeatCalendar events={heatmap} />}
      </div>

      {/* Panel de Seguimiento — actividades solicitadas por Gerencia */}
      <TrackingPanel />

      <DashboardDrillDown
        open={!!drill}
        onClose={() => setDrill(null)}
        payload={drill}
      />
    </div>
  );
}

function HeatCalendar({ events }) {
  const months = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
  const year = new Date().getFullYear();
  const eventsByDate = {};
  events.forEach((e) => {
    if (!eventsByDate[e.date]) eventsByDate[e.date] = [];
    eventsByDate[e.date].push(e);
  });
  const sevColor = (s) => s === "expired" ? "var(--danger)" : s === "critical" ? "#f97316" : s === "warning" ? "var(--warn)" : s === "info" ? "var(--success)" : "var(--bg-3)";
  const [selectedDate, setSelectedDate] = useState(null);
  const selected = selectedDate ? (eventsByDate[selectedDate] || []) : [];

  return (
    <div>
      <div className="grid" style={{ gridTemplateColumns: "repeat(12, 1fr)", gap: 10 }}>
        {months.map((m, i) => {
          const monthIdx = i;
          const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
          return (
            <div key={m}>
              <div className="text-xs font-semibold text-center mb-2">{m}</div>
              <div className="grid" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
                {Array.from({ length: daysInMonth }, (_, d) => {
                  const date = `${year}-${String(monthIdx + 1).padStart(2,"0")}-${String(d+1).padStart(2,"0")}`;
                  const evs = eventsByDate[date] || [];
                  const worst = evs.reduce((acc, e) => {
                    const order = { expired: 4, critical: 3, warning: 2, info: 1, ok: 0 };
                    return order[e.severity] > (order[acc] || -1) ? e.severity : acc;
                  }, null);
                  return (
                    <div
                      key={d}
                      onClick={() => evs.length && setSelectedDate(date)}
                      title={`${date} (${evs.length})`}
                      style={{
                        width: 10, height: 10, borderRadius: 2,
                        background: worst ? sevColor(worst) : "var(--bg-3)",
                        cursor: evs.length ? "pointer" : "default",
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex gap-3 text-xs flex-wrap">
        <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: "var(--danger)", borderRadius: 2 }}></span> Vencido</span>
        <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: "#f97316", borderRadius: 2 }}></span> 7 días</span>
        <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: "var(--warn)", borderRadius: 2 }}></span> 30 días</span>
        <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: "var(--success)", borderRadius: 2 }}></span> 90 días</span>
        <span className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: "var(--bg-3)", borderRadius: 2 }}></span> Sin obligación</span>
      </div>
      {selected.length > 0 && (
        <div className="mt-3 p-3 rounded" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">Obligaciones del {new Date(selectedDate).toLocaleDateString("es-MX")}</div>
            <button className="btn btn-sm" onClick={() => setSelectedDate(null)}><X size={12} /></button>
          </div>
          <div className="grid gap-2">
            {selected.map((e, i) => (
              <div key={e.id || `sel-${i}-${(e.title || "").slice(0, 16)}`} className="text-sm">
                <span className={`badge badge-${e.severity === "expired" || e.severity === "critical" ? "red" : e.severity === "warning" ? "yellow" : "green"}`}>{e.type}</span>
                {" "}<b>{e.title}</b> <span className="text-dim">({e.days_left} días)</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, pct = 0, variant = "", onClick }) {
  return (
    <div
      className={`kpi-card ${variant}`}
      data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
      onClick={onClick}
      onMouseEnter={onClick ? (e) => (e.currentTarget.style.border = "1px solid rgba(29,158,117,0.5)") : undefined}
      onMouseLeave={onClick ? (e) => (e.currentTarget.style.border = "") : undefined}
      style={onClick ? { cursor: "pointer" } : undefined}
      title={onClick ? "Ver detalle →" : undefined}
    >
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      <div className="sub">{sub}</div>
      <div className="bar"><div className="bar-fill" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} /></div>
    </div>
  );
}

function trendClass(ind) {
  const goodUp = ["rme_rec", "rp_rec", "fauna", "reforest", "toa"];
  const badUp = ["spills", "complaints", "noncomply", "emergency"];
  const goodDown = ["water", "diesel", "energy", "gasoline"];
  if (ind.trend === "↑") return goodUp.includes(ind.key) ? "good" : badUp.includes(ind.key) ? "bad" : "neutral";
  if (ind.trend === "↓") return goodDown.includes(ind.key) ? "good" : "neutral";
  return "neutral";
}

function daysUntil(dateStr) {
  if (!dateStr) return "-";
  const diff = Math.round((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

const GROUP_DEFS = [
  { key: "recursos", title: "Grupo 1 — Consumo de Recursos", chart: "line" },
  { key: "aguas", title: "Grupo 2 — Aguas Residuales", chart: "stacked" },
  { key: "residuos", title: "Grupo 3 — Gestión de Residuos", chart: "line" },
  { key: "emisiones", title: "Grupo 4 — Emisiones a la Atmósfera", chart: "bar" },
  { key: "biodiversidad", title: "Grupo 5 — Biodiversidad y Compensaciones", chart: "grouped" },
  { key: "cumplimiento", title: "Grupo 6 — Gestión y Cumplimiento", chart: "stacked" },
  { key: "legal", title: "Grupo 7 — Cumplimiento Legal", chart: "bar" },
  { key: "racs", title: "Grupo 8 — RACs y Hallazgos", chart: "bar" },
  { key: "iso14001", title: "Grupo 9 — ISO 14001 / SGA", chart: "bar" },
  { key: "pemex", title: "Grupo 10 — Soporte PEMEX", chart: "bar" },
];

const GROUP_PALETTE = [
  "#F5B82E", "#4A90E2", "#2EBA7E", "#E53935", "#9B59B6", "#1ABC9C", "#FF8A65", "#C44569",
];

const MONTHS_LBL = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

function buildGroupSeries(group) {
  // Build a Recharts-compatible array [{month, key1, key2, ...}]
  return MONTHS_LBL.map((m, i) => {
    const row = { month: m };
    group.forEach((ind) => {
      row[ind.key] = (ind.monthly_full && ind.monthly_full[i] !== undefined)
        ? ind.monthly_full[i]
        : 0;
    });
    return row;
  });
}

// Map indicator key → {path, state} for navigation drill-down
const INDICATOR_ROUTES = {
  energy:           { path: "/resources",  state: { tab: "energia" } },
  water:            { path: "/resources",  state: { tab: "pozos" } },
  paper:            { path: "/resources",  state: { tab: "otro" } },
  diesel:           { path: "/resources",  state: { tab: "combustibles", sub: "diesel" } },
  gasoline:         { path: "/resources",  state: { tab: "combustibles", sub: "gasoline" } },
  soil_area:        { path: "/resources",  state: { tab: "suelo" } },
  ptar_discharge:   { path: "/resources",  state: { tab: "ptar" } },
  septic_collection:{ path: "/waste",      state: { tab: "FOSAS", subtype: "FOSAS" } },
  trench_collection:{ path: "/waste",      state: { tab: "FOSAS", subtype: "TRINCHERAS" } },
  wash_water:       { path: "/waste",      state: { tab: "FOSAS", subtype: "LAVADO" } },
  hydro_water:      { path: "/waste",      state: { tab: "FOSAS", subtype: "PH" } },
  wastewater_other: { path: "/waste",      state: { tab: "FOSAS", subtype: "OTRA" } },
  rp_disp:          { path: "/waste",      state: { tab: "rp" } },
  rp_rec:           { path: "/waste",      state: { tab: "rp", sub: "valorizacion" } },
  rme_disp:         { path: "/waste",      state: { tab: "rme" } },
  rme_rec:          { path: "/waste",      state: { tab: "rme", sub: "valorizacion" } },
  rsu_disp:         { path: "/waste",      state: { tab: "rsu" } },
  co2eq_total:      { path: "/resources",  state: { tab: "aire" } },
  refrig_emissions: { path: "/resources",  state: { tab: "aire" } },
  compressor_hours: { path: "/resources",  state: { tab: "aire" } },
  generator_hours:  { path: "/resources",  state: { tab: "aire" } },
  welder_hours:     { path: "/resources",  state: { tab: "aire" } },
  raw_materials_total: { path: "/resources", state: { tab: "materia-prima" } },
  chemicals_total:   { path: "/resources", state: { tab: "sustancias-quimicas" } },
  fauna:            { path: "/advc" },
  reforest:         { path: "/advc" },
  restored_area:    { path: "/advc" },
  audits:           { path: "/audits",     state: { tab: "programa" } },
  inspections:      { path: "/audits",     state: { tab: "recorridos" } },
  toa:              { path: "/audits",     state: { tab: "toa" } },
  spills:           { path: "/brigades",   state: { tab: "spills" } },
  complaints:       { path: "/esg",        state: { tab: "quejas" } },
  noncomply:        { path: "/legal",      state: { tab: "evaluacion" } },
  emergency:        { path: "/brigades",   state: { tab: "reportes" } },
  // Group 7 — Cumplimiento Legal
  legal_req_total:        { path: "/legal", state: { tab: "requisitos" } },
  legal_req_comply:       { path: "/legal", state: { tab: "requisitos" } },
  legal_req_noncomply:    { path: "/legal", state: { tab: "requisitos" } },
  legal_permits_active:   { path: "/legal", state: { tab: "permisos" } },
  legal_permits_expired:  { path: "/legal", state: { tab: "permisos" } },
  legal_studies_active:   { path: "/studies" },
  legal_studies_expired:  { path: "/studies" },
  // Group 8 — RACs y Hallazgos
  racs_open:        { path: "/audits", state: { tab: "racs" } },
  racs_closed:      { path: "/audits", state: { tab: "racs" } },
  racs_overdue:     { path: "/audits", state: { tab: "racs" } },
  findings_open:    { path: "/audits", state: { tab: "hallazgos" } },
  findings_closed:  { path: "/audits", state: { tab: "hallazgos" } },
  racs_proc_compliance: { path: "/iso14001", state: { tab: "procedures" } },
  // Group 9 — ISO 14001 / SGA
  iso_procs_total:  { path: "/iso14001", state: { tab: "procedures" } },
  iso_procs_low:    { path: "/iso14001", state: { tab: "procedures" } },
  iso_avg_pct:      { path: "/iso14001", state: { tab: "procedures" } },
  iso_checklists:   { path: "/iso14001", state: { tab: "checklists" } },
  iso_revisions:    { path: "/iso14001", state: { tab: "revisiones" } },
  // Group 10 — PEMEX
  pemex_volumes:    { path: "/pemex", state: { tab: "volumenes" } },
  pemex_solpeds:    { path: "/pemex", state: { tab: "solpeds" } },
  pemex_hes:        { path: "/pemex", state: { tab: "hes" } },
  pemex_supports:   { path: "/pemex", state: { tab: "soportes" } },
  pemex_species_rescue: { path: "/pemex", state: { tab: "rescate" } },
};

function IndicatorGroups({ indicators, onDrill, trendChart }) {
  return (
    <div className="grid gap-4 mb-4" data-testid="indicators-groups">
      {GROUP_DEFS.map((def) => {
        const group = indicators.filter((i) => i.group === def.key);
        if (!group.length) return null;
        const series = buildGroupSeries(group);
        return (
          <div key={def.key} className="card" data-testid={`group-${def.key}`}>
            <div className="card-title flex items-center justify-between">
              <span>{def.title}</span>
              <span className="text-xs text-dim">{group.length} indicadores</span>
            </div>
            <div className="indicator-grid mb-3">
              {group.map((ind, idx) => {
                const color = GROUP_PALETTE[idx % GROUP_PALETTE.length];
                const tcls = trendClass(ind);
                const route = INDICATOR_ROUTES[ind.key] || { path: "/dashboard" };
                return (
                  <div
                    key={`${def.key}-${ind.key}-${idx}`}
                    className="indicator-card indicator-card-clickable"
                    onClick={() => onDrill && onDrill(ind, route)}
                    style={{ cursor: "pointer" }}
                    data-testid={`indicator-${ind.key}`}
                    title="Ver desglose mensual →"
                  >
                    <div className="name">{ind.label}</div>
                    <div className="flex items-center justify-between mt-2">
                      <div>
                        <span className="val">{Number(ind.value).toLocaleString("es-MX")}</span>
                        <span className="unit">{ind.unit}</span>
                      </div>
                      <div className={`trend ${tcls}`}>{ind.trend}</div>
                    </div>
                    <div className="mt-2">
                      <Sparkline data={ind.sparkline || []} width={140} height={26} color={color} testId={`spark-${ind.key}`} />
                    </div>
                  </div>
                );
              })}
            </div>
            <ResponsiveContainer width="100%" height={220}>
              {renderGroupChart(def.chart, series, group)}
            </ResponsiveContainer>
            {def.key === "residuos" && trendChart && trendChart.length > 0 && (
              <div className="mt-3" data-testid="residuos-trend-chart">
                <div className="text-xs text-dim mb-2" style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                  Tendencia mensual de residuos (RP / RME / Agua)
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendChart}>
                    <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                    <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
                    <YAxis stroke="var(--text-dim)" fontSize={11} />
                    <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="rp" stroke="var(--danger)" name="RP (kg)" dot={false} />
                    <Line type="monotone" dataKey="rme" stroke="var(--warn)" name="RME (kg)" dot={false} />
                    <Line type="monotone" dataKey="water" stroke="var(--accent-2)" name="Agua (M³)" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function renderGroupChart(kind, data, group) {
  const palette = GROUP_PALETTE;
  if (kind === "line") {
    return (
      <LineChart data={data}>
        <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
        <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
        <YAxis stroke="var(--text-dim)" fontSize={11} />
        <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {group.map((g, i) => (
          <Line key={g.key} dataKey={g.key} stroke={palette[i % palette.length]} name={g.label} dot={false} />
        ))}
      </LineChart>
    );
  }
  if (kind === "stacked") {
    return (
      <BarChart data={data}>
        <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
        <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
        <YAxis stroke="var(--text-dim)" fontSize={11} />
        <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {group.map((g, i) => (
          <Bar key={g.key} dataKey={g.key} stackId="a" fill={palette[i % palette.length]} name={g.label} />
        ))}
      </BarChart>
    );
  }
  if (kind === "grouped") {
    return (
      <BarChart data={data}>
        <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
        <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
        <YAxis stroke="var(--text-dim)" fontSize={11} />
        <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {group.map((g, i) => (
          <Bar key={g.key} dataKey={g.key} fill={palette[i % palette.length]} name={g.label} />
        ))}
      </BarChart>
    );
  }
  // bar
  return (
    <BarChart data={data}>
      <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
      <XAxis dataKey="month" stroke="var(--text-dim)" fontSize={11} interval={0} />
      <YAxis stroke="var(--text-dim)" fontSize={11} />
      <Tooltip contentStyle={{ background: "var(--bg-2)", border: "1px solid var(--border-2)", borderRadius: 8 }} />
      <Legend wrapperStyle={{ fontSize: 11 }} />
      {group.map((g, i) => (
        <Bar key={g.key} dataKey={g.key} fill={palette[i % palette.length]} name={g.label} />
      ))}
    </BarChart>
  );
}
